import React, { useEffect, useState } from 'react';
import { Modal, Text, Button, Group, Stack, Progress } from '@mantine/core';
import { useAuth } from '../../../hooks/useAuth';

/**
 * SessionTimeoutWarning - Modal that warns users their session is about to expire
 *
 * Displays:
 * - Countdown timer showing time remaining
 * - Option to extend session
 * - Option to logout
 *
 * Automatically shows at 5 minutes before token expiry
 */
export const SessionTimeoutWarning: React.FC = (): React.ReactElement | null => {
  const {
    sessionWarningShown,
    tokenExpiresAt,
    extendSession,
    logout,
    hideSessionWarning
  } = useAuth();

  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const [isExtending, setIsExtending] = useState<boolean>(false);

  // Calculate seconds remaining
  useEffect(() => {
    if (!tokenExpiresAt || !sessionWarningShown) {
      return;
    }

    const updateTimer = (): void => {
      const now = Date.now();
      const expiryMs = new Date(tokenExpiresAt).getTime();
      const msRemaining = expiryMs - now;

      if (msRemaining <= 0) {
        setSecondsRemaining(0);
        // Session has expired - logout handled by ApiClient
        return;
      }

      setSecondsRemaining(Math.floor(msRemaining / 1000));
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => {clearInterval(interval);};
  }, [tokenExpiresAt, sessionWarningShown]);

  // Handle extend session
  const handleExtendSession = async (): Promise<void> => {
    setIsExtending(true);

    try {
      await extendSession();
      // Modal will be hidden by extendSession action
    } catch (error) {
      // Error handled by extendSession
    } finally {
      setIsExtending(false);
    }
  };

  // Handle logout
  const handleLogout = async (): Promise<void> => {
    await logout();
    hideSessionWarning();
  };

  // Don't render if warning not shown
  if (!sessionWarningShown) {
    return null;
  }

  // Format time remaining
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Calculate progress percentage (5 minutes = 300 seconds)
  const progressPercent = (secondsRemaining / 300) * 100;

  return (
    <Modal
      opened={sessionWarningShown}
      onClose={hideSessionWarning}
      title="Session Expiring Soon"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text size="sm">
          Your session will expire in <strong>{timeString}</strong>.
        </Text>

        <Text size="sm" c="dimmed">
          To continue working, click "Stay Logged In" to extend your session.
          Otherwise, you will be logged out automatically.
        </Text>

        <Progress
          value={progressPercent}
          color={secondsRemaining < 60 ? 'red' : secondsRemaining < 120 ? 'yellow' : 'blue'}
          size="sm"
          animated
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={handleLogout}
          >
            Logout Now
          </Button>

          <Button
            onClick={handleExtendSession}
            loading={isExtending}
          >
            Stay Logged In
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
